import React, { useState, useMemo, useRef, useEffect } from "react";
import { gql, useQuery } from "@apollo/client";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import MemoryIcon from "@mui/icons-material/Memory";

// GraphQL Queries
const GET_ZONES_WITH_DEVICES = gql`
    query GetZonesWithDevices($plantId: Int!) {
        zones(where: { plant_id: { equals: $plantId } }) {
            id
            parent_id
            name
            installed_devices {
                id
                name
            }
        }
    }
`;

const GET_ALL_DEVICES = gql`
    query GetAllDevices($plantId: Int!) {
        shadowDevices(where: { plant: { is: { id: { equals: $plantId } } } }) {
            id
            name
        }
    }
`;

export default function HierarchicalDeviceSelector({
    selectedDevices = [],
    onSelectionChange,
    plantId,
    workspaceId,
    loading = false,
}) {
    const [expandedZones, setExpandedZones] = useState(new Set());
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Fetch zones with installed devices
    const { data: zonesData, loading: zonesLoading } = useQuery(
        GET_ZONES_WITH_DEVICES,
        {
            variables: { plantId: plantId ? parseInt(plantId) : 0 },
            skip: !plantId,
        },
    );

    // Fetch all devices
    const { data: devicesData, loading: devicesLoading } = useQuery(
        GET_ALL_DEVICES,
        {
            variables: { plantId: plantId ? parseInt(plantId) : 0 },
            skip: !plantId,
        },
    );

    // Build hierarchical structure
    const hierarchyData = useMemo(() => {
        if (!zonesData?.zones || !devicesData?.shadowDevices) {
            return { zones: [], otherDevices: [] };
        }

        const zones = zonesData.zones;
        const allDevices = devicesData.shadowDevices;

        // Create a map of device IDs that are already in zones
        const deviceIdsInZones = new Set();
        zones.forEach((zone) => {
            zone.installed_devices?.forEach((device) => {
                if (device.id) {
                    deviceIdsInZones.add(device.id);
                }
            });
        });

        // Find root zones (no parent)
        const rootZones = zones.filter((z) => !z.parent_id);

        // Build zone tree recursively
        const buildZoneTree = (zone) => {
            const childZones = zones
                .filter((z) => z.parent_id === zone.id)
                .map((z) => buildZoneTree(z));

            const devices = (zone.installed_devices || [])
                .map((device) => {
                    return {
                        id: device.id,
                        name: device.id, // Device name is in the id field
                        type: "device",
                    };
                })
                .filter((d) => d.id);

            return {
                id: `zone-${zone.id}`,
                name: zone.name,
                type: "zone",
                children: [...childZones, ...devices],
                zoneId: zone.id,
            };
        };

        const zoneTree = rootZones.map((z) => buildZoneTree(z));

        // Find devices not in any zone
        const otherDevices = allDevices.filter(
            (dev) => !deviceIdsInZones.has(dev.id),
        );

        // Create synthetic "All Zones" root node
        const allZonesNode = {
            id: "zone-all",
            name: "All Zones",
            type: "zone",
            children: zoneTree,
            zoneId: "all",
        };

        return {
            zones: [allZonesNode],
            otherDevices,
        };
    }, [zonesData, devicesData]);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setDropdownOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Expand all zones when dropdown opens
    useEffect(() => {
        if (dropdownOpen && hierarchyData.zones.length > 0) {
            const getAllZoneIds = (node) => {
                const ids = [node.id];
                if (node.children) {
                    node.children.forEach((child) => {
                        if (child.type === "zone") {
                            ids.push(...getAllZoneIds(child));
                        }
                    });
                }
                return ids;
            };

            const allZoneIds = new Set();
            hierarchyData.zones.forEach((zone) => {
                getAllZoneIds(zone).forEach((id) => allZoneIds.add(id));
            });

            setExpandedZones(allZoneIds);
        }
    }, [dropdownOpen, hierarchyData]);

    // Get all selectable IDs for a node recursively (devices, or leaf empty zones)
    const getSelectableIds = (node) => {
        if (node.type === "device") {
            return [node.id];
        }
        if (node.type === "zone") {
            if (!node.children || node.children.length === 0) {
                return [node.id];
            }
            const childIds = node.children.flatMap((child) => getSelectableIds(child));
            if (childIds.length === 0) {
                return [node.id];
            }
            return childIds;
        }
        return [];
    };

    // Check if node or any descendants are selected
    const isNodeSelected = (node) => {
        if (node.type === "device") {
            return selectedDevices.includes(node.id);
        }
        if (node.type === "zone") {
            const selectableIds = getSelectableIds(node);
            if (selectableIds.length === 0) return false;
            return selectableIds.every((id) => selectedDevices.includes(id));
        }
        return false;
    };

    // Check if node is partially selected
    const isNodePartiallySelected = (node) => {
        if (node.type === "device") {
            return false;
        }
        if (node.type === "zone") {
            const selectableIds = getSelectableIds(node);
            if (selectableIds.length === 0) return false;
            const selectedCount = selectableIds.filter((id) =>
                selectedDevices.includes(id),
            ).length;
            return selectedCount > 0 && selectedCount < selectableIds.length;
        }
        return false;
    };

    // Handle checkbox change
    const handleNodeChange = (node, checked) => {
        let newSelection = [...selectedDevices];

        if (node.type === "device") {
            if (checked) {
                if (!newSelection.includes(node.id)) {
                    newSelection.push(node.id);
                }
            } else {
                newSelection = newSelection.filter((id) => id !== node.id);
            }
        } else if (node.type === "zone") {
            const selectableIds = getSelectableIds(node);
            if (checked) {
                newSelection = [...new Set([...newSelection, ...selectableIds])];
            } else {
                newSelection = newSelection.filter(
                    (id) => !selectableIds.includes(id),
                );
            }
        }

        onSelectionChange(newSelection);
    };

    // Toggle zone expansion
    const toggleZoneExpanded = (zoneId) => {
        const newExpanded = new Set(expandedZones);
        if (newExpanded.has(zoneId)) {
            newExpanded.delete(zoneId);
        } else {
            newExpanded.add(zoneId);
        }
        setExpandedZones(newExpanded);
    };

    // Render tree node
    const renderNode = (node, depth = 0) => {
        const isExpanded = expandedZones.has(node.id);
        const hasChildren = node.children && node.children.length > 0;
        const isSelected = isNodeSelected(node);
        const isPartial = isNodePartiallySelected(node);

        return (
            <div key={node.id} style={{ marginLeft: `${depth * 16}px` }}>
                <div className="tw-flex tw-items-center tw-gap-2 tw-py-1">
                    {hasChildren ? (
                        <button
                            className="tw-p-0 tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center hover:tw-bg-slate-200 tw-rounded"
                            onClick={() => toggleZoneExpanded(node.id)}
                        >
                            {isExpanded ? (
                                <ExpandMoreIcon fontSize="small" />
                            ) : (
                                <ChevronRightIcon fontSize="small" />
                            )}
                        </button>
                    ) : (
                        <div className="tw-w-5" />
                    )}

                    <input
                        type="checkbox"
                        className="hierarchical-device-selector-checkbox tw-cursor-pointer"
                        checked={isSelected}
                        ref={(el) => {
                            if (el && isPartial) {
                                el.indeterminate = true;
                            }
                        }}
                        onChange={(e) =>
                            handleNodeChange(node, e.target.checked)
                        }
                    />

                    <span
                        className={`tw-text-xs tw-text-slate-900 ${node.type === "zone" ? "tw-font-semibold" : "tw-font-normal"} tw-truncate tw-whitespace-nowrap tw-flex tw-items-center tw-gap-1.5`}
                    >
                        {node.type === "zone" ? (
                            <GpsFixedIcon
                                fontSize="inherit"
                                className="hierarchical-device-selector-zone-icon tw-flex-shrink-0"
                                style={{ fontSize: "0.875rem" }}
                            />
                        ) : (
                            <MemoryIcon
                                fontSize="inherit"
                                className="hierarchical-device-selector-device-icon tw-flex-shrink-0"
                                style={{ fontSize: "0.875rem" }}
                            />
                        )}
                        {node.name}
                    </span>
                </div>

                {hasChildren && isExpanded && (
                    <div>
                        {node.children.map((child) =>
                            renderNode(child, depth + 1),
                        )}
                    </div>
                )}
            </div>
        );
    };

    if (zonesLoading || devicesLoading) {
        return (
            <div className="tw-relative tw-w-full">
                <button
                    disabled
                    className="tw-mt-1 tw-w-full tw-border tw-border-slate-300 tw-px-2 tw-py-1.5 tw-text-xs tw-text-slate-500 tw-bg-slate-50 tw-text-left"
                >
                    Loading...
                </button>
            </div>
        );
    }

    const displayText =
        selectedDevices.length === 0
            ? "Select devices..."
            : `${selectedDevices.length} device${selectedDevices.length !== 1 ? "s" : ""} selected`;

    return (
        <div ref={dropdownRef} className="tw-relative tw-w-full">
            {/* Dropdown Button */}
            <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="hierarchical-device-selector-btn tw-mt-1 tw-w-full tw-border tw-border-slate-300 tw-px-2 tw-py-1.5 tw-text-xs tw-text-black tw-bg-white tw-text-left tw-flex tw-items-center tw-justify-between focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 focus:tw-border-blue-500"
            >
                <span className="tw-truncate">{displayText}</span>
                {dropdownOpen ? (
                    <ExpandLessIcon
                        fontSize="small"
                        className="hierarchical-device-selector-icon tw-ml-2 tw-flex-shrink-0"
                    />
                ) : (
                    <ExpandMoreIcon
                        fontSize="small"
                        className="hierarchical-device-selector-icon tw-ml-2 tw-flex-shrink-0"
                    />
                )}
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
                <div className="hierarchical-device-selector-dropdown tw-absolute tw-top-full tw-left-0 tw-right-0 tw-mt-1 tw-border tw-border-slate-300 tw-rounded tw-p-2 tw-max-h-96 tw-overflow-y-auto tw-bg-white tw-shadow-lg tw-z-50 tw-min-w-max">
                    {/* Zones Hierarchy */}
                    {hierarchyData.zones.length > 0 && (
                        <div className="tw-mb-4">
                            {hierarchyData.zones.map((zone) =>
                                renderNode(zone),
                            )}
                        </div>
                    )}

                    {/* Other Devices */}
                    {hierarchyData.otherDevices.length > 0 && (
                        <div>
                            <div className="tw-font-semibold tw-text-xs tw-text-slate-700 tw-mb-2">
                                Other Devices
                            </div>
                            {hierarchyData.otherDevices.map((device) =>
                                renderNode({
                                    id: device.id,
                                    name: device.id,
                                    type: "device",
                                }),
                            )}
                        </div>
                    )}

                    {hierarchyData.zones.length === 0 &&
                        hierarchyData.otherDevices.length === 0 && (
                            <div className="tw-text-xs tw-text-slate-500">
                                No devices or zones available
                            </div>
                        )}
                </div>
            )}
        </div>
    );
}
